import { useEffect, useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useForm } from 'react-hook-form'
import { adminApi, usersApi } from '../../api/settings'
import { PageSpinner } from '../../components/ui/Spinner'
import { Modal } from '../../components/ui/Modal'
import { ConfirmDialog } from '../../components/ui/ConfirmDialog'
import { DropdownSelect } from '../../components/ui/DropdownSelect'
import { formatDate, fullName } from '../../lib/utils'
import { Pencil, UserX, UserCheck, Trash2, ShieldCheck, Users as UsersIcon, Lock, UserPlus } from 'lucide-react'
import toast from 'react-hot-toast'

interface AdminUser {
  id: string
  email: string
  firstName: string
  lastName: string
  role: 'administrator' | 'company_owner' | 'sales_user'
  companyId: string | null
  isActive: boolean
  createdAt: string
}

interface CompanyRow {
  id: string
  name: string
}

/**
 * Admin → Users. Cross-tenant directory split into two tables:
 *
 *  1. Company Owners — Edit only (no Disable, no Delete). Owners are tied
 *     to a tenant's lifecycle; deactivate the company instead. Backend
 *     enforces this in usersService.softRemove and the per-tenant
 *     deactivate route would still work but the UI doesn't expose it.
 *  2. Sales Users — Edit + Disable + Delete. Disable flips isActive=false
 *     (recoverable). Delete soft-removes (sets deletedAt; row hidden from
 *     the list). Both backend operations enforce can't-delete-yourself.
 *
 * Edit modal locks `companyId` for both tables — admins move users by
 * deleting + recreating, never via PATCH (which would orphan all of
 * their assigned leads/deals/activities).
 *
 * Administrators are NOT shown in either table — admins manage admins
 * outside this UI surface.
 */
export function UsersPage() {
  useEffect(() => { document.title = 'Users' }, [])
  const qc = useQueryClient()
  const [editTarget, setEditTarget] = useState<AdminUser | null>(null)
  const [disableTarget, setDisableTarget] = useState<AdminUser | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<AdminUser | null>(null)
  const [showInvite, setShowInvite] = useState(false)

  const { data: users, isLoading: usersLoading } = useQuery({
    queryKey: ['admin-users'],
    queryFn: () => adminApi.listUsers().then(r => r.data as AdminUser[]),
  })

  const { data: companies } = useQuery({
    queryKey: ['admin-companies'],
    queryFn: () => adminApi.listCompanies().then(r => r.data as CompanyRow[]),
  })

  const companyNameById = useMemo(() => {
    const map = new Map<string, string>()
    companies?.forEach(c => map.set(c.id, c.name))
    return map
  }, [companies])

  const { owners, salesUsers } = useMemo(() => {
    const owners: AdminUser[] = []
    const salesUsers: AdminUser[] = []
    for (const u of users ?? []) {
      if (u.role === 'company_owner') owners.push(u)
      else if (u.role === 'sales_user') salesUsers.push(u)
      // administrators are intentionally hidden from this page
    }
    return { owners, salesUsers }
  }, [users])

  const disableMutation = useMutation({
    mutationFn: (u: AdminUser) => usersApi.deactivate(u.companyId!, u.id),
    onSuccess: () => {
      toast.success('User disabled')
      qc.invalidateQueries({ queryKey: ['admin-users'] })
      setDisableTarget(null)
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message
      toast.error(typeof msg === 'string' ? msg : 'Failed to disable user')
    },
  })

  // Activate uses the existing PATCH endpoint (UpdateUserDto includes
  // isActive). No separate confirm — re-enabling is benign.
  const activateMutation = useMutation({
    mutationFn: (u: AdminUser) => usersApi.update(u.companyId!, u.id, { isActive: true }),
    onSuccess: () => {
      toast.success('User activated')
      qc.invalidateQueries({ queryKey: ['admin-users'] })
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message
      toast.error(typeof msg === 'string' ? msg : 'Failed to activate user')
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (u: AdminUser) => adminApi.deleteUser(u.id),
    onSuccess: () => {
      toast.success('User deleted')
      qc.invalidateQueries({ queryKey: ['admin-users'] })
      setDeleteTarget(null)
    },
    onError: (err: any) => {
      const msg = err?.response?.data?.message
      toast.error(typeof msg === 'string' ? msg : 'Failed to delete user')
    },
  })

  if (usersLoading) return <PageSpinner />

  return (
    <div className="p-6 space-y-8">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="page-title">Users</h1>
          <p className="text-xs text-gray-500 mt-1">
            Cross-tenant directory of company owners and sales users.
            Administrators are managed outside this view.
          </p>
        </div>
        <button onClick={() => setShowInvite(true)} className="btn btn-primary btn-sm flex items-center gap-1.5">
          <UserPlus className="w-4 h-4" /> Invite User
        </button>
      </div>

      <UserTable
        title="Company Owners"
        icon={ShieldCheck}
        users={owners}
        companyNameById={companyNameById}
        onEdit={u => setEditTarget(u)}
        // Owners get no Disable / Delete — see component header comment.
      />

      <UserTable
        title="Sales Users"
        icon={UsersIcon}
        users={salesUsers}
        companyNameById={companyNameById}
        onEdit={u => setEditTarget(u)}
        onDisable={u => setDisableTarget(u)}
        onActivate={u => activateMutation.mutate(u)}
        onDelete={u => setDeleteTarget(u)}
      />

      <Modal
        open={!!editTarget}
        onClose={() => setEditTarget(null)}
        title={editTarget ? `Edit ${fullName(editTarget.firstName, editTarget.lastName)}` : ''}
        size="sm"
      >
        {editTarget && (
          <EditUserForm
            target={editTarget}
            companyName={editTarget.companyId ? companyNameById.get(editTarget.companyId) ?? '—' : '—'}
            onSuccess={() => {
              setEditTarget(null)
              qc.invalidateQueries({ queryKey: ['admin-users'] })
            }}
          />
        )}
      </Modal>

      <ConfirmDialog
        isOpen={!!disableTarget}
        onClose={() => setDisableTarget(null)}
        onConfirm={() => disableTarget && disableMutation.mutate(disableTarget)}
        title="Disable user?"
        message={
          disableTarget
            ? `${fullName(disableTarget.firstName, disableTarget.lastName)} will be blocked from signing in. They stay in the list as Inactive and can be re-enabled later.`
            : ''
        }
        confirmLabel="Disable"
        isPending={disableMutation.isPending}
      />

      <ConfirmDialog
        isOpen={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteTarget && deleteMutation.mutate(deleteTarget)}
        title="Delete user?"
        message={
          deleteTarget
            ? `${fullName(deleteTarget.firstName, deleteTarget.lastName)} will be removed from the list. The row stays in the database (soft-delete) but the user can no longer sign in or appear anywhere.`
            : ''
        }
        confirmLabel="Delete"
        isPending={deleteMutation.isPending}
      />

      <Modal
        open={showInvite}
        onClose={() => setShowInvite(false)}
        title="Invite User"
        size="sm"
      >
        <InviteUserForm
          companies={companies ?? []}
          onSuccess={() => {
            setShowInvite(false)
            qc.invalidateQueries({ queryKey: ['admin-users'] })
          }}
        />
      </Modal>
    </div>
  )
}

function UserTable({
  title, icon: Icon, users, companyNameById, onEdit, onDisable, onActivate, onDelete,
}: {
  title: string
  icon: typeof ShieldCheck
  users: AdminUser[]
  companyNameById: Map<string, string>
  onEdit: (u: AdminUser) => void
  onDisable?: (u: AdminUser) => void
  onActivate?: (u: AdminUser) => void
  onDelete?: (u: AdminUser) => void
}) {
  return (
    <section>
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-4 h-4 text-gray-500" />
        <h2 className="text-base font-semibold text-gray-900">{title}</h2>
        <span className="text-xs text-gray-400">({users.length})</span>
      </div>
      <div className="card overflow-hidden">
        <table className="table">
          <thead>
            <tr>
              <th>Name</th><th>Email</th><th>Workspace</th><th>Status</th><th>Joined</th>
              <th className="text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.length === 0 && (
              <tr><td colSpan={6} className="text-center py-8 text-gray-500 text-sm">No {title.toLowerCase()} yet</td></tr>
            )}
            {users.map(u => (
              <tr key={u.id}>
                <td className="font-medium">{fullName(u.firstName, u.lastName)}</td>
                <td className="text-gray-600">{u.email}</td>
                <td className="text-gray-500 text-xs">{u.companyId ? companyNameById.get(u.companyId) ?? '—' : '—'}</td>
                <td>
                  <span className={`badge ${u.isActive ? 'badge-green' : 'badge-gray'}`}>
                    {u.isActive ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="text-gray-500 text-xs">{formatDate(u.createdAt)}</td>
                <td>
                  <div className="flex justify-end gap-1">
                    <button
                      onClick={() => onEdit(u)}
                      className="btn btn-ghost btn-sm p-1 text-gray-600"
                      aria-label="Edit user"
                      title="Edit user"
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                    {onDisable && onActivate && (
                      u.isActive ? (
                        <button
                          onClick={() => onDisable(u)}
                          className="btn btn-ghost btn-sm p-1 text-amber-700 hover:bg-amber-50"
                          aria-label="Disable user"
                          title="Disable user"
                        >
                          <UserX className="w-4 h-4" />
                        </button>
                      ) : (
                        <button
                          onClick={() => onActivate(u)}
                          className="btn btn-ghost btn-sm p-1 text-green-700 hover:bg-green-50"
                          aria-label="Activate user"
                          title="Activate user"
                        >
                          <UserCheck className="w-4 h-4" />
                        </button>
                      )
                    )}
                    {onDelete && (
                      <button
                        onClick={() => onDelete(u)}
                        className="btn btn-ghost btn-sm p-1 text-red-600 hover:bg-red-50"
                        aria-label="Delete user"
                        title="Delete user"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  )
}

function InviteUserForm({ companies, onSuccess }: { companies: CompanyRow[]; onSuccess: () => void }) {
  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<{
    email: string; firstName: string; lastName: string
    role: 'company_owner' | 'sales_user'; companyId: string
  }>({ defaultValues: { role: 'sales_user' } })

  const companyId = watch('companyId')

  const mutation = useMutation({
    mutationFn: (dto: { email: string; firstName: string; lastName: string; role: string; companyId: string }) => {
      if (dto.role === 'company_owner') {
        return adminApi.createCompanyOwner(dto.companyId, { email: dto.email, firstName: dto.firstName, lastName: dto.lastName })
      }
      return adminApi.createSalesUser(dto.companyId, { email: dto.email, firstName: dto.firstName, lastName: dto.lastName })
    },
    onSuccess: () => { toast.success('Invitation sent'); onSuccess() },
    onError: (err: any) => {
      const msg = err?.response?.data?.message
      toast.error(typeof msg === 'string' ? msg : Array.isArray(msg) ? msg.join('. ') : 'Failed to invite user')
    },
  })

  return (
    <form onSubmit={handleSubmit(d => mutation.mutate(d))} className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">First Name *</label>
          <input {...register('firstName', { required: true })} className="input" />
        </div>
        <div>
          <label className="label">Last Name *</label>
          <input {...register('lastName', { required: true })} className="input" />
        </div>
      </div>
      <div>
        <label className="label">Email *</label>
        <input {...register('email', { required: true })} type="email" className="input" />
      </div>
      <div>
        <label className="label">Role *</label>
        <DropdownSelect
          value={watch('role') ?? 'sales_user'}
          onChange={v => setValue('role', v as 'sales_user' | 'company_owner')}
          options={[{ value: 'sales_user', label: 'Sales User' }, { value: 'company_owner', label: 'Company Owner' }]}
        />
      </div>
      <div>
        <label className="label">Workspace *</label>
        <DropdownSelect
          value={watch('companyId') ?? ''}
          onChange={v => setValue('companyId', v)}
          placeholder="Select workspace…"
          options={companies.map(c => ({ value: c.id, label: c.name }))}
          searchable={companies.length > 5}
        />
        {errors.companyId && <p className="text-xs text-red-600 mt-1">Workspace is required</p>}
      </div>
      <p className="text-xs text-gray-500">
        An invitation email will be sent. The user sets their own password via the link.
      </p>
      <div className="flex justify-end pt-2">
        <button
          type="submit"
          disabled={mutation.isPending || !companyId}
          className="btn btn-primary btn-sm"
        >
          {mutation.isPending ? 'Sending…' : 'Send invitation'}
        </button>
      </div>
    </form>
  )
}

function EditUserForm({
  target, companyName, onSuccess,
}: {
  target: AdminUser; companyName: string; onSuccess: () => void
}) {
  const { register, handleSubmit, formState: { isDirty } } = useForm<{
    firstName: string; lastName: string; email: string
  }>({
    defaultValues: {
      firstName: target.firstName,
      lastName: target.lastName,
      email: target.email,
    },
  })

  const mutation = useMutation({
    // Per-tenant route — admin bypasses CompanyGuard. UpdateUserDto already
    // excludes companyId (forbidNonWhitelisted) so the lock is enforced
    // at the API too, not just visually.
    mutationFn: (dto: { firstName: string; lastName: string; email: string }) =>
      usersApi.update(target.companyId!, target.id, dto),
    onSuccess: () => { toast.success('User updated'); onSuccess() },
    onError: (err: any) => {
      const msg = err?.response?.data?.message
      toast.error(typeof msg === 'string' ? msg : Array.isArray(msg) ? msg.join('. ') : 'Failed to update user')
    },
  })

  return (
    <form onSubmit={handleSubmit(d => mutation.mutate(d))} className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label">First Name *</label>
          <input {...register('firstName', { required: true })} className="input" />
        </div>
        <div>
          <label className="label">Last Name *</label>
          <input {...register('lastName', { required: true })} className="input" />
        </div>
      </div>
      <div>
        <label className="label">Email *</label>
        <input {...register('email', { required: true })} type="email" className="input" />
      </div>
      <div>
        <label className="label flex items-center gap-1">
          <Lock className="w-3 h-3 text-gray-400" /> Workspace
        </label>
        <input value={companyName} disabled readOnly className="input" />
        <p className="text-xs text-gray-500 mt-1">
          Workspace assignment can't be changed here. To move a user, delete and recreate them in the target workspace.
        </p>
      </div>
      <div className="flex justify-end pt-2">
        <button
          type="submit"
          disabled={!isDirty || mutation.isPending}
          className="btn btn-primary btn-sm"
        >
          {mutation.isPending ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </form>
  )
}
